import { zodResolver } from "@hookform/resolvers/zod"
import { CheckIcon, XIcon } from "lucide-react"
import { useMemo } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
  useNavigate,
  useSearchParams,
} from "react-router-dom"

import BrandLogo from "@/components/commons/app-brand-logo"
import { LoaderButton } from "@/components/commons/loader-button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { FRONTEND_ROUTES } from "@/constants"
import { useWhitelabel } from "@/contexts/whitelabel"
import { toast } from "@/hooks/use-toast"
import { cn } from "@/lib/utils"
import type { AlterPasswordSchemaProps } from "@/schemas/alter-password"
import { alterPasswordSchema } from "@/schemas/alter-password"
import { forgotPasswordConfirm } from "@/services"

import { PasswordInput } from "../ui/password-input"

export const RecoveryPasswordForm = () => {
  const { t } = useTranslation()
  const { configuration } = useWhitelabel()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const brand = import.meta.env.VITE_BRAND ?? "pay2b"

  const emailFromUrl = searchParams.get("email")
  const emailValue = emailFromUrl || ""

  const schema = useMemo(() => alterPasswordSchema(t), [t])
  const form = useForm<AlterPasswordSchemaProps>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      email: emailValue,
      password: "",
      confirmPassword: "",
      code: "",
    },
  })
  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      await forgotPasswordConfirm(values)
      toast({
        title: t("auth.passwordResetSuccess"),
        variant: "success",
      })
      navigate(FRONTEND_ROUTES.LOGIN.href)
    } catch {
      toast({
        title: t("auth.passwordResetError"),
        variant: "destructive",
      })
    }
  })
  // eslint-disable-next-line react-hooks/incompatible-library
  const password = form.watch("password")
  const confirmPassword = form.watch("confirmPassword")
  const hasUpperCase = /[A-Z]/.test(password)
  const hasNumber = /[0-9]/.test(password)
  const hasSpecialChar =
    /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
  const isPending = form.formState.isSubmitting
  return (
    <div className="w-full max-w-[400px] space-y-8">
      {/* Logo e Título */}
      <div className="flex flex-col items-center space-y-6">
        <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl p-3">
          <div
            className="absolute inset-0 rounded-2xl"
            style={{
              backgroundColor: "var(--whitelabel-primary)",
              opacity: 0.1,
            }}
          />
          <BrandLogo
            brand={brand}
            className="relative z-10 h-10 w-10"
            customLogo={configuration?.merchant?.logo}
            customAlt={configuration?.merchant?.fantasyName}
          />
        </div>
        <div className="space-y-2 text-center">
          <h1 className="page-title tracking-tight text-foreground">
            {t("auth.createNewPassword")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("auth.enterCodeAndPassword")}
          </p>
        </div>
      </div>
      {/* Formulário */}
      <div className="flex flex-col rounded-2xl border border-border/50 bg-card p-8 shadow-lg shadow-black/5">
        <div className="mb-6 space-y-2">
          <p className="text-center text-sm font-medium text-foreground">
            {t("auth.passwordRequirements")}
          </p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-1">
              {hasUpperCase && (
                <CheckIcon
                  size={12}
                  className="text-primary"
                />
              )}
              {!hasUpperCase && password.length > 0 && (
                <XIcon size={14} className="text-red-500" />
              )}
              {password.length === 0 && (
                <div className="size-1.5 rounded-full bg-zinc-900" />
              )}
              <p
                className={cn(
                  hasUpperCase && "text-primary",
                  !hasUpperCase &&
                    password.length > 0 &&
                    "text-red-500"
                )}
              >
                {t("auth.uppercaseLetter")}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {hasSpecialChar && (
                <CheckIcon
                  size={12}
                  className="text-primary"
                />
              )}
              {!hasSpecialChar && password.length > 0 && (
                <XIcon size={14} className="text-red-500" />
              )}
              {password.length === 0 && (
                <div className="size-1.5 rounded-full bg-zinc-900" />
              )}
              <p
                className={cn(
                  hasSpecialChar && "text-primary",
                  !hasSpecialChar &&
                    password.length > 0 &&
                    "text-red-500"
                )}
              >
                {t("auth.specialCharacter")}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {hasNumber && (
                <CheckIcon
                  size={12}
                  className="text-primary"
                />
              )}
              {!hasNumber && password.length > 0 && (
                <XIcon size={14} className="text-red-500" />
              )}
              {password.length === 0 && (
                <div className="size-1.5 rounded-full bg-zinc-900" />
              )}
              <p
                className={cn(
                  hasNumber && "text-primary",
                  !hasNumber &&
                    password.length > 0 &&
                    "text-red-500"
                )}
              >
                {t("auth.number")}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {password.length >= 8 && (
                <CheckIcon
                  size={12}
                  className="text-primary"
                />
              )}
              {password.length < 8 &&
                password.length > 0 && (
                  <XIcon
                    size={14}
                    className="text-red-500"
                  />
                )}
              {password.length === 0 && (
                <div className="size-1.5 rounded-full bg-zinc-900" />
              )}
              <p
                className={cn(
                  password.length >= 8 && "text-primary",
                  password.length < 8 &&
                    password.length !== 0 &&
                    "text-red-500"
                )}
              >
                {t("auth.minCharacters")}
              </p>
            </div>
          </div>
        </div>

        <Form {...form}>
          <form
            method="POST"
            onSubmit={handleSubmit}
            className="space-y-5"
          >
            <FormField
              name="email"
              control={form.control}
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel className="text-sm font-medium text-foreground">
                    {t("auth.email")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="text"
                      placeholder="user@email.com"
                      disabled={isPending}
                      className="h-12 rounded-xl border-border/50 bg-background px-4 transition-all focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="code"
              control={form.control}
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel className="text-sm font-medium text-foreground">
                    {t("auth.code")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="text"
                      placeholder={t(
                        "auth.codeReceivedByEmail"
                      )}
                      disabled={isPending}
                      className="h-12 rounded-xl border-border/50 bg-background px-4 transition-all focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="password"
              control={form.control}
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel className="text-sm font-medium text-foreground">
                    {t("auth.password")}
                  </FormLabel>
                  <FormControl>
                    <PasswordInput
                      {...field}
                      onChange={(e) => {
                        field.onChange(e)
                        if (confirmPassword.length > 0) {
                          form.trigger("confirmPassword")
                        }
                      }}
                      placeholder={t("auth.password")}
                      disabled={isPending}
                      className="h-12 rounded-xl border-border/50 bg-background px-4 transition-all focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="confirmPassword"
              control={form.control}
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel className="text-sm font-medium text-foreground">
                    {t("auth.confirmPassword")}
                  </FormLabel>
                  <FormControl>
                    <PasswordInput
                      {...field}
                      placeholder={t(
                        "auth.confirmPassword"
                      )}
                      disabled={isPending}
                      className="h-12 rounded-xl border-border/50 bg-background px-4 transition-all focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="pt-2">
              <LoaderButton
                isLoading={isPending}
                type="submit"
                className="btn-whitelabel h-12 w-full rounded-xl text-base font-medium shadow-lg transition-all hover:shadow-xl"
              >
                {t("auth.confirm")}
              </LoaderButton>
            </div>
          </form>
        </Form>
      </div>
    </div>
  )
}
